'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdBed,
    MdClose,
    MdFilterList,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    getBeds,
    deleteBed,
    Bed,
    BedFilters,
    BED_TYPE_LABELS,
    BED_STATUS_LABELS,
    BED_STATUS_COLORS,
} from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function BedsPage() {
    const navigate = useNavigate()
    const [beds, setBeds] = useState<Bed[]>([])
    const [filteredBeds, setFilteredBeds] = useState<Bed[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [bedToDelete, setBedToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [filters, setFilters] = useState<BedFilters>({})
    const [showFilters, setShowFilters] = useState(false)
    const {
        hospitals,
        loading: loadingHospitals,
        selectedHospitalId,
        setSelected,
    } = useHospitals()

    useEffect(() => {
        loadBeds()
    }, [filters, selectedHospitalId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = beds.filter(
                bed =>
                    bed.number
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    bed.unit?.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
            setFilteredBeds(filtered)
        } else {
            setFilteredBeds(beds)
        }
    }, [searchTerm, beds])

    const loadBeds = async () => {
        setLoading(true)
        try {
            const response = await getBeds(undefined, {
                ...filters,
                hospitalId: selectedHospitalId || undefined,
            })
            if (response.success) {
                setBeds(response.result)
                setFilteredBeds(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar leitos')
            }
        } catch (error) {
            toast.error('Erro ao carregar leitos')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (id: number) => {
        setBedToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!bedToDelete) return

        setDeleting(true)
        try {
            const response = await deleteBed(bedToDelete)
            if (response.success) {
                toast.success('Leito excluído com sucesso')
                loadBeds()
                setShowDeleteModal(false)
                setBedToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir leito')
            }
        } catch (error) {
            toast.error('Erro ao excluir leito')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando leitos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdBed className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Leitos
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {beds.length}{' '}
                            {beds.length === 1 ? 'leito' : 'leitos'} cadastrados
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={() => navigate('/hospital/beds/new')}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Adicionar</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <HospitalSelector
                    hospitals={hospitals}
                    selectedId={selectedHospitalId}
                    onChange={setSelected}
                    loading={loadingHospitals}
                />
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por número ou unidade..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                    <button
                        className="btn btn-outline gap-2"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList className="text-lg" />
                        Filtros
                    </button>
                </div>

                {showFilters && (
                    <div className="mt-4 pt-4 border-t border-base-300/50 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Status</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={filters.status || ''}
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        status:
                                            (e.target.value as any) ||
                                            undefined,
                                    })
                                }
                            >
                                <option value="">Todos</option>
                                {Object.entries(BED_STATUS_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Tipo</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={filters.type || ''}
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        type:
                                            (e.target.value as any) ||
                                            undefined,
                                    })
                                }
                            >
                                <option value="">Todos</option>
                                {Object.entries(BED_TYPE_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">
                                    Disponibilidade
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={
                                    filters.available === undefined
                                        ? ''
                                        : filters.available
                                          ? 'true'
                                          : 'false'
                                }
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        available:
                                            e.target.value === ''
                                                ? undefined
                                                : e.target.value === 'true',
                                    })
                                }
                            >
                                <option value="">Todos</option>
                                <option value="true">Disponíveis</option>
                                <option value="false">Indisponíveis</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {filteredBeds.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdBed className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhum leito encontrado'
                                : 'Nenhum leito cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando leitos às unidades'}
                        </p>
                        {!searchTerm && (
                            <button
                                onClick={() => navigate('/hospital/beds/new')}
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeiro leito
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filteredBeds.map(bed => (
                        <div
                            key={bed.id}
                            className="bg-base-100 rounded-xl border border-base-300/50 p-4 cursor-pointer hover:shadow-lg transition-all"
                            onClick={() => navigate(`/hospital/beds/${bed.id}`)}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center">
                                    <MdBed className="text-2xl text-secondary" />
                                </div>
                                <span
                                    className={`badge ${BED_STATUS_COLORS[bed.status]}`}
                                >
                                    {BED_STATUS_LABELS[bed.status]}
                                </span>
                            </div>
                            <h3 className="font-bold text-lg mb-1">
                                Leito {bed.number}
                            </h3>
                            <p className="text-sm text-base-content/70 mb-2">
                                {bed.unit?.name || 'Sem unidade'}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-base-content/60">
                                <span className="badge badge-ghost badge-sm">
                                    {BED_TYPE_LABELS[bed.type]}
                                </span>
                                {bed.isPrivate && (
                                    <span className="badge badge-secondary badge-sm">
                                        Privativo
                                    </span>
                                )}
                            </div>
                            {bed.currentAdmission && (
                                <div className="mt-3 pt-3 border-t border-base-300/50">
                                    <p className="text-sm font-medium">
                                        {bed.currentAdmission.client.name}
                                    </p>
                                </div>
                            )}
                            <div className="flex justify-end gap-1 mt-3">
                                <button
                                    onClick={e => {
                                        e.stopPropagation()
                                        navigate(
                                            `/hospital/beds/${bed.id}/editar`
                                        )
                                    }}
                                    className="btn btn-ghost btn-sm"
                                    title="Editar"
                                >
                                    <MdEdit className="text-lg" />
                                </button>
                                <button
                                    onClick={e => {
                                        e.stopPropagation()
                                        handleDelete(bed.id)
                                    }}
                                    className="btn btn-ghost btn-sm text-error hover:bg-error/10"
                                    title="Excluir"
                                >
                                    <MdDelete className="text-lg" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setBedToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir leito"
                message="Tem certeza de que deseja excluir este leito? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
